"use client";

import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent,
  type RefObject,
} from "react";

function readPx(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writePx(key: string, value: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(Math.round(value)));
  } catch {
    // Quota / private-mode failures are non-fatal; ignore.
  }
}

function fallbackMaxHeightPx(): number {
  if (typeof window === "undefined") return 520;
  return Math.min(window.innerHeight * 0.6, 520);
}

function resolveMaxHeightPx(
  minPx: number,
  getMaxHeightPx?: () => number,
): number {
  if (!getMaxHeightPx) return fallbackMaxHeightPx();
  const measured = getMaxHeightPx();
  if (!Number.isFinite(measured) || measured <= 0) {
    return fallbackMaxHeightPx();
  }
  return Math.max(minPx, Math.floor(measured));
}

function clampHeight(
  px: number,
  minPx: number,
  getMaxHeightPx?: () => number,
): number {
  return Math.min(Math.max(px, minPx), resolveMaxHeightPx(minPx, getMaxHeightPx));
}

export interface UsePersistedPopoverHeightOptions {
  storageKey: string;
  defaultPx?: number;
  minPx?: number;
  /**
   * Returns the maximum popover height in px (e.g. from trigger bottom to
   * preview panel bottom). When omitted, uses a viewport-based fallback.
   */
  getMaxHeightPx?: () => number;
  /** Re-clamp height when this element is resized (e.g. preview chart root). */
  observeBoundsRef?: RefObject<Element | null>;
}

export function usePersistedPopoverHeight({
  storageKey,
  defaultPx = 320,
  minPx = 160,
  getMaxHeightPx,
  observeBoundsRef,
}: UsePersistedPopoverHeightOptions) {
  const getMaxHeightPxRef = useRef(getMaxHeightPx);
  getMaxHeightPxRef.current = getMaxHeightPx;

  const clamp = useCallback(
    (px: number) => clampHeight(px, minPx, getMaxHeightPxRef.current),
    [minPx],
  );

  const [heightPx, setHeightPx] = useState(() =>
    clampHeight(readPx(storageKey, defaultPx), minPx, getMaxHeightPx),
  );
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const heightPxRef = useRef(heightPx);
  heightPxRef.current = heightPx;

  useLayoutEffect(() => {
    if (!getMaxHeightPxRef.current) return;

    const reclamp = () => {
      setHeightPx((prev) => clamp(prev));
    };

    reclamp();

    const el = observeBoundsRef?.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", reclamp);
      return () => window.removeEventListener("resize", reclamp);
    }

    const observer = new ResizeObserver(reclamp);
    observer.observe(el);
    window.addEventListener("resize", reclamp);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", reclamp);
    };
  }, [clamp, observeBoundsRef]);

  const onResizePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragRef.current = { startY: e.clientY, startHeight: heightPxRef.current };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [],
  );

  const onResizePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const delta = e.clientY - dragRef.current.startY;
      const next = clamp(dragRef.current.startHeight + delta);
      setHeightPx(next);
    },
    [clamp],
  );

  const endResize = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      dragRef.current = null;
      writePx(storageKey, heightPxRef.current);
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    [storageKey],
  );

  const resizeHandleProps = {
    role: "separator" as const,
    "aria-orientation": "horizontal" as const,
    "aria-label": "Resize inputs panel",
    className:
      "relative flex shrink-0 h-2.5 w-full cursor-ns-resize touch-none select-none items-center justify-center border-t border-border after:absolute after:inset-x-0 after:top-1/2 after:h-1 after:-translate-y-1/2",
    onPointerDown: onResizePointerDown,
    onPointerMove: onResizePointerMove,
    onPointerUp: endResize,
    onPointerCancel: endResize,
  };

  return { heightPx, resizeHandleProps };
}
